import { Trans } from '@lingui/react'
import { clsx } from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { Component, Fragment } from 'react'

import {
  PAGE_TYPE_SELLER_PRODUCTS,
  TENANT_IT,
  TENANTS_TO_SHOW_RX_NOT_IN_CATALOG,
} from 'shared/consts'
import {
  AD_SLOT_SEARCH_HITS_1,
  AD_SLOT_SEARCH_HITS_2,
  AD_SLOT_SEARCH_TOP_BANNER,
} from 'shared/consts/adSlots'
import { GTM_EVENT_TYPE_CLICK } from 'shared/consts/gtm'
import {
  PAGE_TYPE_BRAND_PAGE,
  PAGE_TYPE_CATEGORY,
  PAGE_TYPE_SEARCH_RESULTS,
} from 'shared/consts/page'
import { discountExperimentRemoveAllDiscountsFromAlgoliaHits } from 'shared/experiments/components/DiscountExperimentFR/utils'
import LoadMoreButtonExperiment from 'shared/experiments/components/SerpLoadMore'
import { NEW_CDN } from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import store from 'shared/store'
import {
  REFRESH_MODALS,
  SEARCH_RESULTS_HITS_UPDATED,
} from 'shared/store/ducks/events'
import { isAdServerEnabled } from 'shared/utils/adServerUtils'
import { getSortingItems } from 'shared/utils/algolia/filters'
import { getProperty } from 'shared/utils/algolia/resultsState'
import noop from 'shared/utils/noop'
import AdBanner from 'views/components/molecules/AdBanner'
import AdSlot from 'views/components/molecules/AdSlot'
import Modal from 'views/components/molecules/Modal'
import OdsModalContent from 'views/components/organisms/BuyBoxWithVariants/BuyBoxOdsButtonAndModal/OdsModalContent'
import { IMAGE_LAZY_LOAD_START_INDEX } from 'views/components/organisms/FilteredProductList/consts'
import FilteredProductListItem from 'views/components/organisms/FilteredProductList/FilteredProductListItem'
import SearchProductListItem from 'views/components/organisms/FilteredProductList/SearchProductListItem'
import OdsTransitionModal from 'views/components/organisms/OdsTransitionModal'
import SellerModal from 'views/components/organisms/SellerModal'
import { getEntryHasVolumePricing } from 'views/components/organisms/VolumePricingModal/utils/volumePricingAccessor'
import {
  ALGOLIA_SEARCH_LIST_HOOK,
  ALGOLIA_SEARCH_LIST_SELECTOR,
  HITS_BASE_CLASSNAME,
} from 'views/consts'
import { isUseMarketplaceVolumeBasedPricesEnabled } from 'views/utils/featureToggleUtils'
import {
  isPrescriptionProduct,
  isProductAvailable,
  isProductNotInCatalog,
  isProductOutOfStock,
} from 'views/utils/productUtils'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'
import { hitsType } from 'views/utils/propTypes/hits'

import {
  convertAlgoliaHitToProductProps,
  getAlgoliaUserDataProductsByType,
} from './helpers'

import './Hits.scss'

export class Hits extends Component {
  state = this.getInitialState()

  constructor(props) {
    super(props)
    this.filteredItems = []
    this.enableSponsoredLabel = true
  }

  getInitialState() {
    const {
      publicRuntimeConfig: {
        pageProperties: { resultsState },
      },
    } = this.props

    // Sponsored products should they the same, regardless of filtering or navigation
    const userData = getProperty(resultsState, 'userData', null)
    const sponsoredProducts = getAlgoliaUserDataProductsByType(
      userData,
      'labels'
    )
    const highlightedProducts = getAlgoliaUserDataProductsByType(
      userData,
      'highlighted'
    )

    return {
      sponsoredProducts,
      highlightedProducts,
    }
  }

  componentDidMount() {
    // Trigger initial page handlers
    store.dispatch({ type: SEARCH_RESULTS_HITS_UPDATED })
  }

  shouldComponentUpdate(nextProps) {
    const { queryID: currentQueryID, i18n, indexName } = this.props
    const { queryID: nextQueryID } = nextProps
    const { events } = store.getState()

    if (this.filteredItems.length === 0 && i18n) {
      this.filteredItems = getSortingItems({
        indexName,
        i18n,
        isSearchResultsPage: true,
      })
    }

    this.enableSponsoredLabel =
      !events?.searchState ||
      events?.searchState?.sortBy === this.filteredItems[0]?.value

    return nextQueryID !== currentQueryID
  }

  componentDidUpdate() {
    // Restore labels with modals
    store.dispatch({
      type: REFRESH_MODALS,
      selector: ALGOLIA_SEARCH_LIST_SELECTOR,
    })

    store.dispatch({ type: SEARCH_RESULTS_HITS_UPDATED })
  }

  shouldRenderAdSlotAtTheBottom = (entries, slotPosition) =>
    slotPosition >= entries.length

  getQueryParams(product, position) {
    const { query, queryID } = this.props
    const { objectID } = product

    return {
      eventType: GTM_EVENT_TYPE_CLICK,
      objectIDs: `[${objectID}]`,
      position,
      query,
      queryID,
    }
  }

  getSlotConditionals(entries) {
    const { firstAdSlotPosition, secondAdSlotPosition } = this.props

    const shouldRenderAdSlotAtTheBottom = () =>
      this.shouldRenderAdSlotAtTheBottom(entries, firstAdSlotPosition)
    const shouldRenderFirstAdSlot = index =>
      !shouldRenderAdSlotAtTheBottom() && index + 1 === firstAdSlotPosition
    const shouldRenderSecondAdSlot = index =>
      entries.length >= secondAdSlotPosition &&
      index + 1 === secondAdSlotPosition

    return {
      shouldRenderFirstAdSlot,
      shouldRenderSecondAdSlot,
      shouldRenderAdSlotAtTheBottom,
    }
  }

  shouldShowRxNotInCatalog(product, tenant) {
    return (
      TENANTS_TO_SHOW_RX_NOT_IN_CATALOG.includes(tenant) &&
      isPrescriptionProduct(product) &&
      isProductNotInCatalog(product.stockStatus)
    )
  }
  getListEntries() {
    const {
      hits,
      publicRuntimeConfig: {
        publicConfig: { cdnUrl, superScripts, currency, tenant } = {},
        pageProperties: { experiments },
      },
    } = this.props
    const { sponsoredProducts, highlightedProducts } = this.state

    let products = []

    const cdnEnabled = getExperiment(NEW_CDN, experiments)

    hits.forEach(hit => {
      let id
      if (sponsoredProducts[hit.id]) {
        id = hit.id
      } else if (sponsoredProducts[hit.pzn]) {
        id = hit.pzn
      }

      const sponsored = Boolean(id)
      if (sponsored) {
        hit.sponsored = true
        hit.sponsoredProductLabel = sponsoredProducts[id]
      } else {
        // a product should not be highlighted if it is sponsored
        if (highlightedProducts[hit.id]) {
          id = hit.id
        } else if (highlightedProducts[hit.pzn]) {
          id = hit.pzn
        }

        if (id) {
          hit.highlighted = true
          hit.highlightedProductLabel = highlightedProducts[id]
        }
      }
      hit.cdnUrl = cdnUrl

      const convertedProduct = convertAlgoliaHitToProductProps({
        product: {
          ...hit,
          position: hit.metadata?.position,
        },
        superScripts,
        isRebrandingEnabled: tenant === TENANT_IT,
        currency,
        placeholderFromCdn: cdnEnabled.isEnabled,
      })

      products.push(convertedProduct)
    })

    return products.filter(hit => {
      const { stockStatus, name } = hit
      return (
        (isProductAvailable(stockStatus) ||
          isProductOutOfStock(stockStatus) ||
          this.shouldShowRxNotInCatalog(hit, tenant)) &&
        name !== ''
      )
    })
  }

  shouldRenderNewLayout() {
    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { pageType, experiments },
      },
    } = this.props

    switch (pageType) {
      case PAGE_TYPE_SEARCH_RESULTS:
        return true
      case PAGE_TYPE_CATEGORY:
      case PAGE_TYPE_SELLER_PRODUCTS:
        return isMarketplace(experiments, tenant) || tenant !== TENANT_IT
      case PAGE_TYPE_BRAND_PAGE:
        return isMarketplace(experiments, tenant)
      default:
        return false
    }
  }

  render() {
    const {
      publicRuntimeConfig: {
        publicConfig: { onlineDoctorConfig } = {},
        pageProperties: { adPlacements, experiments, pageType, featureToggles },
      },
    } = this.props

    let entries = this.getListEntries()

    if (!entries?.length) {
      return null
    }

    entries = discountExperimentRemoveAllDiscountsFromAlgoliaHits({
      hits: entries,
      experiments,
    })

    const {
      shouldRenderAdSlotAtTheBottom,
      shouldRenderFirstAdSlot,
      shouldRenderSecondAdSlot,
    } = this.getSlotConditionals(entries)

    const shouldRenderNewLayout = this.shouldRenderNewLayout()
    const adSlotProps = {
      position: 'center',
      wide: true,
      paddingTop: !shouldRenderNewLayout,
      noPadding: false,
      dataAttributes: { 'data-is-search-page': shouldRenderNewLayout },
    }

    let topBannerPlacement

    let firstAdBanner
    let secondAdBanner

    if (isAdServerEnabled(experiments)) {
      topBannerPlacement = adPlacements?.find(
        item => item?.divName === AD_SLOT_SEARCH_TOP_BANNER
      )

      const firstAdBannerPlacement = adPlacements?.find(
        item => item?.divName === AD_SLOT_SEARCH_HITS_1
      )

      if (firstAdBannerPlacement) {
        firstAdBanner = (
          <AdBanner
            placement={firstAdBannerPlacement}
            id={AD_SLOT_SEARCH_HITS_1}
            renderBannerOnServerSide
          />
        )
      }

      const secondAdBannerPlacement = adPlacements?.find(
        item => item?.divName === AD_SLOT_SEARCH_HITS_2
      )

      if (secondAdBannerPlacement) {
        secondAdBanner = (
          <AdBanner
            placement={secondAdBannerPlacement}
            id={AD_SLOT_SEARCH_HITS_2}
            renderBannerOnServerSide
          />
        )
      }
    } else {
      firstAdBanner = <AdSlot id={AD_SLOT_SEARCH_HITS_1} {...adSlotProps} />
      secondAdBanner = <AdSlot id={AD_SLOT_SEARCH_HITS_2} {...adSlotProps} />
    }

    return (
      <>
        {topBannerPlacement && (
          <AdBanner
            placement={topBannerPlacement}
            id={AD_SLOT_SEARCH_TOP_BANNER}
            renderBannerOnServerSide
            className="o-Hits__TopSlot"
          />
        )}
        <ul
          className={clsx(HITS_BASE_CLASSNAME, 'a-list-reset', {
            'u-margin-large--bottom': shouldRenderAdSlotAtTheBottom(),
          })}
          data-clientside-hook={ALGOLIA_SEARCH_LIST_HOOK}
        >
          {entries.map((product, index) => {
            const lazyLoadImage = index >= IMAGE_LAZY_LOAD_START_INDEX
            const renderFirstAdSlot = shouldRenderFirstAdSlot(index)
            const renderSecondAdSlot = shouldRenderSecondAdSlot(index)
            const offerHasVolumeBasedPrices =
              getEntryHasVolumePricing(product) &&
              isUseMarketplaceVolumeBasedPricesEnabled(featureToggles)

            const queryParams = this.getQueryParams(product, index + 1)

            let item = (
              <FilteredProductListItem
                {...queryParams}
                lazyLoadImage={lazyLoadImage}
                product={product}
                queryParams={queryParams}
              />
            )

            if (shouldRenderNewLayout) {
              item = (
                <SearchProductListItem
                  enableSponsoredLabel={this.enableSponsoredLabel}
                  isFirst={index === 0}
                  lazyLoadImage={lazyLoadImage}
                  product={product}
                  queryParams={queryParams}
                  showVolumePricingLabel={offerHasVolumeBasedPrices}
                />
              )
            }

            return (
              <Fragment key={product.id}>
                {item}
                <>
                  {renderFirstAdSlot && firstAdBanner && (
                    <li className="u-overflow-hidden">{firstAdBanner}</li>
                  )}
                  {renderSecondAdSlot && secondAdBanner && (
                    <li className="u-overflow-hidden">{secondAdBanner}</li>
                  )}
                </>
              </Fragment>
            )
          })}
        </ul>
        {onlineDoctorConfig?.enabled && (
          <>
            <OdsTransitionModal pageType={pageType} />
            <Modal
              id={`modalIdOdsSERP`}
              size="medium"
              title={<Trans id="pdp.buyboxwithvariants.odsModal.title" />}
            >
              <OdsModalContent />
            </Modal>
          </>
        )}
        {![PAGE_TYPE_CATEGORY, PAGE_TYPE_BRAND_PAGE].includes(pageType) && (
          <SellerModal />
        )}
        {shouldRenderAdSlotAtTheBottom() && firstAdBanner}
        <LoadMoreButtonExperiment {...this.props} />
      </>
    )
  }
}

Hits.propTypes = {
  currentPage: number.isRequired,
  firstAdSlotPosition: number,
  hasMore: bool,
  hits: arrayOf(shape(hitsType)).isRequired,
  hitsPerPage: number.isRequired,
  i18n: i18nType,
  indexName: string.isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  query: string,
  queryID: string.isRequired,
  refineNext: func,
  secondAdSlotPosition: number,
}

Hits.defaultProps = {
  firstAdSlotPosition: 3,
  hasMore: false,
  query: '',
  refineNext: noop,
  secondAdSlotPosition: 7,
}
